import * as React from 'react';
import { ISuggestion } from './new-checkout/address-input';

export interface ISuggestionDropdownProps {
    suggestions: ISuggestion[];
    inputId?: string;
    inputElement: HTMLInputElement | null;
    onSuggestionSelected: (suggestion: ISuggestion) => void;
}

/**
 * Simple suggestion dropdown component for Azure Maps suggestions.
 * @param props - Configuration of the functional component.
 * @returns React functional component.
 */
export const SuggestionDropdownComponent: React.FC<ISuggestionDropdownProps> = (props: ISuggestionDropdownProps) => {
    const { suggestions, inputId, inputElement, onSuggestionSelected } = props;
    const [isFocused, setIsFocused] = React.useState(false);

    const isVisible = isFocused && suggestions.length > 0;

    // Handle input focus state
    React.useEffect(() => {
        const handleFocus = () => {
            setIsFocused(true);
        };

        const handleBlur = () => {
            setIsFocused(false);
        };

        if (inputElement) {
            inputElement.addEventListener('focus', handleFocus);
            inputElement.addEventListener('blur', handleBlur);
        }

        return () => {
            if (inputElement) {
                inputElement.removeEventListener('focus', handleFocus);
                inputElement.removeEventListener('blur', handleBlur);
            }
        };
    }, [inputElement]);

    if (!isVisible) {
        return null;
    }

    const handleSuggestionClick = (suggestion: ISuggestion) => {
        onSuggestionSelected(suggestion);
        setIsFocused(false);
    };

    const suggestionListView = suggestions.map((suggestion: ISuggestion, index: number) => (
        <li
            key={suggestion.title}
            id={`suggestion-${index}`}
            role='listitem'
            aria-label={`${suggestion.formattedSuggestion}, item ${index + 1}`}
            onMouseDown={() => handleSuggestionClick(suggestion)}
            className='msc-suggestion-dropdown__item'
        >
            <div className='msc-suggestion-dropdown__content'>
                <div className='msc-suggestion-dropdown__icon'></div>
                <div className='msc-suggestion-dropdown__text'>
                    <p className='msc-suggestion-dropdown__title'>{suggestion.title}</p>
                    <p className='msc-suggestion-dropdown__subtitle'>{suggestion.subtitle}</p>
                </div>
            </div>
        </li>
    ));

    return (
        <div className={'msc-suggestion-dropdown'}>
            <div
                className='msc-suggestion-dropdown__container'
                id={`suggestionContainer_${inputId}`}
                role='listbox'
                aria-label='Address suggestions'
            >
                <div className='msc-suggestion-dropdown__wrapper'>
                    <ul role='list' aria-live='polite'>
                        {suggestionListView}
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default SuggestionDropdownComponent;
